




import React, { useState, useEffect, useRef } from "react";

const Skills = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animatedPercentages, setAnimatedPercentages] = useState({});
  const sectionRef = useRef(null);

  const skills = [
    { name: "Solidity", percentage: 90, icon: "⚡", color: "from-yellow-400 to-orange-500" },
    { name: "Ethereum", percentage: 90, icon: "💎", color: "from-blue-400 to-purple-500" },
    { name: "Hyperledger Fabric", percentage: 95, icon: "🔗", color: "from-green-400 to-blue-500" },
    { name: "Hardhat", percentage: 90, icon: "🔨", color: "from-red-400 to-pink-500" },
    { name: "web3.js / ethers.js", percentage: 90, icon: "🌐", color: "from-cyan-400 to-blue-500" },
    { name: "React.js / Next.js", percentage: 80, icon: "⚛️", color: "from-blue-500 to-cyan-400" },
    { name: "Node.js / Express", percentage: 80, icon: "🟢", color: "from-green-500 to-emerald-400" },
    { name: "MongoDB / MySQL", percentage: 80, icon: "🗄️", color: "from-purple-400 to-indigo-500" },
    { name: "Docker", percentage: 70, icon: "🐳", color: "from-blue-600 to-cyan-500" },
    { name: "Git", percentage: 90, icon: "📚", color: "from-orange-400 to-red-500" },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Animate skill percentages
          skills.forEach((skill, index) => {
            setTimeout(() => {
              animatePercentage(skill.name, skill.percentage);
            }, index * 200);
          });
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const animatePercentage = (skillName, targetPercentage) => {
    let currentPercentage = 0;
    const increment = targetPercentage / 50;
    
    const timer = setInterval(() => {
      currentPercentage += increment;
      if (currentPercentage >= targetPercentage) {
        currentPercentage = targetPercentage;
        clearInterval(timer);
      }
      
      setAnimatedPercentages(prev => ({
        ...prev,
        [skillName]: Math.round(currentPercentage)
      }));
    }, 30);
  };

  const SkillCard = ({ skill, index, isLeft }) => {
    const [isHovered, setIsHovered] = useState(false);
    const animatedPercentage = animatedPercentages[skill.name] || 0;

    return (
      <div
        className={`skill-card transform transition-all duration-500 ${
          isVisible 
            ? `translate-x-0 opacity-100` 
            : `${isLeft ? '-translate-x-16' : 'translate-x-16'} opacity-0`
        }`}
        style={{ 
          transitionDelay: `${index * 150}ms`,
          animation: isVisible ? `fadeInBounce 0.8s ease-out ${index * 150}ms both` : 'none'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={`
          relative p-6 rounded-xl bg-white shadow-lg hover:shadow-2xl
          transform transition-all duration-300 hover:-translate-y-2
          border border-gray-100 hover:border-transparent
          ${isHovered ? 'scale-105' : 'scale-100'}
        `}>
          {/* Animated background gradient on hover */}
          <div className={`
            absolute inset-0 rounded-xl bg-gradient-to-r ${skill.color} opacity-0
            transition-opacity duration-300 ${isHovered ? 'opacity-10' : 'opacity-0'}
          `} />
          
          {/* Skill header */}
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <span className={`
                text-2xl transition-transform duration-300
                ${isHovered ? 'scale-125 rotate-12' : 'scale-100'}
              `}>
                {skill.icon}
              </span>
              <span className={`
                font-bold text-gray-800 transition-colors duration-300
                ${isHovered ? 'text-transparent bg-clip-text bg-gradient-to-r ' + skill.color : ''}
              `}>
                {skill.name}
              </span>
            </div>
            <div className={`
              text-2xl font-bold transition-all duration-500
              ${isHovered 
                ? `text-transparent bg-clip-text bg-gradient-to-r ${skill.color} scale-110` 
                : 'text-blue-600'
              }
            `}>
              {animatedPercentage}%
            </div>
          </div>

          {/* Progress bar container */}
          <div className="relative">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              {/* Animated background pulse */}
              <div className={`
                absolute inset-0 bg-gradient-to-r ${skill.color} opacity-20 rounded-full
                ${isHovered ? 'animate-pulse' : ''}
              `} />
              
              {/* Main progress bar */}
              <div
                className={`
                  h-full bg-gradient-to-r ${skill.color} rounded-full
                  transition-all duration-1000 ease-out relative overflow-hidden
                  ${isHovered ? 'shadow-lg' : ''}
                `}
                style={{ width: `${animatedPercentage}%` }}
              >
                {/* Shimmer effect */}
                <div className={`
                  absolute inset-0 -skew-x-12 opacity-40
                  bg-gradient-to-r from-transparent via-white to-transparent
                  ${isHovered ? 'animate-shimmer' : ''}
                `} />
                
                {/* Glow effect */}
                <div className={`
                  absolute inset-0 bg-gradient-to-r ${skill.color}
                  ${isHovered ? 'blur-sm opacity-50' : 'opacity-0'}
                  transition-opacity duration-300
                `} />
              </div>
            </div>

            {/* Skill level indicator */}
            <div className={`
              absolute -top-1 transition-all duration-500 transform
              ${isHovered ? '-translate-y-1 scale-110' : ''}
            `}
            style={{ left: `${Math.max(animatedPercentage - 2, 0)}%` }}>
              <div className={`
                w-5 h-5 bg-gradient-to-r ${skill.color} rounded-full
                shadow-lg ${isHovered ? 'animate-bounce' : ''}
              `} />
            </div>
          </div>

          {/* Floating particles effect */}
          {isHovered && (
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className={`
                    absolute w-1 h-1 bg-gradient-to-r ${skill.color} rounded-full
                    animate-float opacity-60
                  `}
                  style={{
                    left: `${20 + i * 15}%`,
                    top: `${30 + (i % 2) * 20}%`,
                    animationDelay: `${i * 200}ms`,
                    animationDuration: '3s'
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <style jsx>{`
        @keyframes fadeInBounce {
          0% { opacity: 0; transform: translateY(30px) scale(0.9); }
          50% { transform: translateY(-5px) scale(1.02); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0; }
          50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        .animate-float {
          animation: float 3s infinite ease-in-out;
        }
      `}</style>

      <section ref={sectionRef} id="skills" className="py-20 bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 min-h-screen relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-blue-200 rounded-full opacity-20 animate-pulse" />
        <div className="absolute top-40 right-20 w-32 h-32 bg-purple-200 rounded-full opacity-20 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-green-200 rounded-full opacity-20 animate-pulse" style={{ animationDelay: '2s' }} />

        <div className="container mx-auto px-4 relative z-10">
          {/* Section Title */}
          <div className={`
            text-center mb-16 transition-all duration-1000
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
          `}>
            <div className="inline-block p-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full mb-4">
              <div className="bg-white rounded-full px-6 py-2">
                <span className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  TECHNICAL EXPERTISE
                </span>
              </div>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent">
              Skills & Technologies
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Core technical skills and cutting-edge tools leveraged to build decentralized 
              and full-stack solutions that shape the future of blockchain technology.
            </p>
          </div>

          {/* Skills Grid */}
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                {skills.slice(0, 5).map((skill, index) => (
                  <SkillCard key={skill.name} skill={skill} index={index} isLeft={true} />
                ))}
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {skills.slice(5).map((skill, index) => (
                  <SkillCard key={skill.name} skill={skill} index={index + 5} isLeft={false} />
                ))}
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className={`
            text-center mt-20 transition-all duration-1000 delay-1000
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
          `}>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl blur-lg opacity-75 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl p-8 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <span className="text-3xl animate-bounce">🚀</span>
                  <h3 className="text-2xl md:text-3xl font-bold">
                    Always Learning & Growing
                  </h3>
                  <span className="text-3xl animate-bounce" style={{ animationDelay: '0.5s' }}>⚡</span>
                </div>
                <p className="text-lg md:text-xl opacity-90 max-w-4xl mx-auto leading-relaxed">
                  Continuously exploring new blockchain protocols, DeFi innovations, 
                  and emerging technologies to stay at the forefront of the 
                  decentralized revolution.
                </p>
                
                {/* Animated progress indicators */}
                <div className="flex justify-center space-x-8 mt-6">
                  {['Innovation', 'Growth', 'Excellence'].map((item, index) => (
                    <div key={item} className="text-center">
                      <div className={`
                        w-12 h-12 mx-auto mb-2 bg-white bg-opacity-20 rounded-full 
                        flex items-center justify-center font-bold text-lg
                        animate-pulse
                      `} style={{ animationDelay: `${index * 0.5}s` }}>
                        {index + 1}
                      </div>
                      <span className="text-sm font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Skills;

// import React from "react";
// import { motion } from "framer-motion";

// const Skills = () => {
//   const skills = [
//     { name: "Solidity", percentage: 85 },
//     { name: "Ethereum", percentage: 85 },
//     { name: "Hyperledger Fabric", percentage: 80 },
//     { name: "Hardhat", percentage: 75 },
//     { name: "web3.js / ethers.js", percentage: 75 },
//     { name: "React.js / Next.js", percentage: 80 },
//     { name: "Node.js / Express", percentage: 80 },
//     { name: "MongoDB / MySQL", percentage: 70 },
//     { name: "Docker", percentage: 70 },
//     { name: "Git", percentage: 90 },
//   ];

//   return (
//     <section id="skills" className="py-20 bg-gray-50">
//       <div className="container mx-auto px-4">
//         {/* Section Title */}
//         <motion.div
//           initial={{ opacity: 0, y: 20 }}
//           whileInView={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.8 }}
//           viewport={{ once: true }}
//           className="text-center mb-16"
//         >
//           <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
//             Skills
//           </h2>
//           <p className="text-xl text-gray-600 max-w-3xl mx-auto">
//             Core technical skills and tools leveraged to build decentralized and
//             full-stack solutions.
//           </p>
//         </motion.div>

//         <div className="max-w-4xl mx-auto">
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
//             {/* Left Column */}
//             <div className="space-y-6">
//               {skills.slice(0, 5).map((skill, index) => (
//                 <motion.div
//                   key={skill.name}
//                   initial={{ opacity: 0, x: -50 }}
//                   whileInView={{ opacity: 1, x: 0 }}
//                   transition={{ duration: 0.6, delay: index * 0.1 }}
//                   viewport={{ once: true }}
//                   className="group"
//                 >
//                   <div className="flex justify-between items-center mb-2">
//                     <span className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
//                       {skill.name}
//                     </span>
//                     <span className="text-blue-600 font-medium">
//                       {skill.percentage}%
//                     </span>
//                   </div>
//                   <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
//                     <motion.div
//                       initial={{ width: 0 }}
//                       whileInView={{ width: `${skill.percentage}%` }}
//                       transition={{ duration: 1.5, delay: index * 0.1 + 0.5 }}
//                       viewport={{ once: true }}
//                       className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full shadow-lg"
//                     />
//                   </div>
//                 </motion.div>
//               ))}
//             </div>

//             {/* Right Column */}
//             <div className="space-y-6">
//               {skills.slice(5).map((skill, index) => (
//                 <motion.div
//                   key={skill.name}
//                   initial={{ opacity: 0, x: 50 }}
//                   whileInView={{ opacity: 1, x: 0 }}
//                   transition={{ duration: 0.6, delay: index * 0.1 }}
//                   viewport={{ once: true }}
//                   className="group"
//                 >
//                   <div className="flex justify-between items-center mb-2">
//                     <span className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
//                       {skill.name}
//                     </span>
//                     <span className="text-blue-600 font-medium">
//                       {skill.percentage}%
//                     </span>
//                   </div>
//                   <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
//                     <motion.div
//                       initial={{ width: 0 }}
//                       whileInView={{ width: `${skill.percentage}%` }}
//                       transition={{ duration: 1.5, delay: index * 0.1 + 0.5 }}
//                       viewport={{ once: true }}
//                       className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full shadow-lg"
//                     />
//                   </div>
//                 </motion.div>
//               ))}
//             </div>
//           </div>
//         </div>

//         {/* Additional Skills */}
//         <motion.div
//           initial={{ opacity: 0, y: 20 }}
//           whileInView={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.8, delay: 0.4 }}
//           viewport={{ once: true }}
//           className="text-center mt-16"
//         >
//           <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white max-w-4xl mx-auto">
//             <h3 className="text-2xl font-bold mb-4">
//               Always Learning & Growing
//             </h3>
//             <p className="text-lg opacity-90">
//               Continuously exploring new blockchain protocols, DeFi innovations,
//               and emerging technologies to stay at the forefront of the
//               industry.
//             </p>
//           </div>
//         </motion.div>
//       </div>
//     </section>
//   );
// };

// export default Skills;
